import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, HouseAccountRow, HouseAccountStatement } from '../api';
import { houseAccountEndpoints, organizationEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars, toCents } from './event/shared';

/**
 * House accounts (D-173).
 *
 * What an organization owes, what is left of its limit, and the statement
 * behind the number. The balance is derived from the charges and the
 * settlements rather than stored, so nothing here can disagree with the
 * payments it is made of.
 *
 * Deliberately not invoicing. The booking system issues invoices and chases
 * them; this says what is owed and records what came in.
 */

const today = () => new Date().toISOString().slice(0, 10);

export default function HouseAccountsPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const accounts = useQuery({ queryKey: ['house-accounts'], queryFn: houseAccountEndpoints.list, enabled: can('house_accounts.manage') });
  const organizations = useQuery({ queryKey: ['organizations'], queryFn: organizationEndpoints.list, enabled: can('house_accounts.manage') });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['house-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['house-account', open] });
  };

  if (!can('house_accounts.manage')) return <NotPermitted permission="house_accounts.manage" />;

  const rows = accounts.data?.accounts ?? [];
  const owed = rows.reduce((sum, row) => sum + row.balance_cents, 0);

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title="House accounts"
        description="Organizations that may charge at the window, what they owe, and what is left of the limit. A settlement is money received against the account, not against one order."
        actions={<span className="text-sm text-ink-600">{dollars(owed)} outstanding</span>}
      >
        <Table headers={['Organization', 'Balance', 'Limit', 'Available', 'Terms', 'Oldest charge', '']}>
          {rows.length === 0 ? (
            <EmptyRow columns={7}>No house accounts yet. Turn one on below.</EmptyRow>
          ) : (
            rows.map((account) => (
              <tr key={account.organization_id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">
                  <button type="button" className="text-accent underline-offset-2 hover:underline" onClick={() => setOpen(open === account.organization_id ? null : account.organization_id)}>
                    {account.name}
                  </button>
                </td>
                <td className="tabular px-5 py-3 text-sm">{dollars(account.balance_cents)}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{account.limit_cents === null ? 'no limit' : dollars(account.limit_cents)}</td>
                <td className="tabular px-5 py-3 text-sm">
                  {account.available_cents === null ? (
                    '—'
                  ) : (
                    <Badge tone={account.available_cents === 0 ? 'bad' : account.available_cents < (account.limit_cents ?? 0) / 5 ? 'warn' : 'ok'}>{dollars(account.available_cents)}</Badge>
                  )}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{account.terms_days} days</td>
                <td className="px-5 py-3 text-sm text-ink-600">{account.oldest_unsettled_on ?? '—'}</td>
                <td className="px-5 py-3 text-right">
                  <Button onClick={() => setOpen(open === account.organization_id ? null : account.organization_id)}>{open === account.organization_id ? 'Close' : 'Statement'}</Button>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      <AccountSettings organizations={organizations.data?.organizations ?? []} accounts={rows} onError={setError} onChanged={refresh} />

      {open ? <StatementPanel organizationId={open} onError={setError} onChanged={refresh} /> : null}
    </div>
  );
}

/** Turning an account on, and what it may run up. */
function AccountSettings({
  organizations,
  accounts,
  onError,
  onChanged,
}: {
  organizations: { id: string; name: string }[];
  accounts: HouseAccountRow[];
  onError: (message: string | null) => void;
  onChanged: () => void;
}) {
  const [organizationId, setOrganizationId] = useState('');
  const [limit, setLimit] = useState('');
  const [terms, setTerms] = useState('30');
  const [email, setEmail] = useState('');
  const existing = accounts.find((row) => row.organization_id === organizationId);

  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      houseAccountEndpoints.set(organizationId, {
        enabled,
        // Empty means no ceiling, which is a real answer for the venue's own
        // departments — not a ceiling of zero.
        limit_cents: limit.trim() === '' ? null : toCents(limit),
        terms_days: Number(terms) || 30,
        statement_email: email.trim() || null,
      }),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (cause: ApiError) => onError(cause.message),
  });

  return (
    <Panel title="Set up an account" description="An organization is not a house account until somebody says so. Leave the limit empty for no ceiling.">
      <div className="flex flex-wrap items-end gap-3 px-5 py-4">
        <Field label="Organization" htmlFor="house-org">
          <select
            id="house-org"
            className={INPUT}
            value={organizationId}
            onChange={(event) => {
              setOrganizationId(event.target.value);
              const row = accounts.find((account) => account.organization_id === event.target.value);
              setLimit(row?.limit_cents != null ? (row.limit_cents / 100).toFixed(2) : '');
              setTerms(String(row?.terms_days ?? 30));
              setEmail(row?.statement_email ?? '');
            }}
          >
            <option value="">Choose…</option>
            {organizations.map((organization) => (
              <option key={organization.id} value={organization.id}>
                {organization.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Limit" htmlFor="house-limit" hint="Empty for no ceiling">
          <input id="house-limit" className={`tabular w-28 ${INPUT}`} value={limit} onChange={(event) => setLimit(event.target.value)} placeholder="5000.00" />
        </Field>
        <Field label="Terms (days)" htmlFor="house-terms">
          <input id="house-terms" className={`tabular w-20 ${INPUT}`} value={terms} onChange={(event) => setTerms(event.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label="Statement email" htmlFor="house-email">
          <input id="house-email" className={`w-56 ${INPUT}`} value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Button variant="primary" disabled={!organizationId || save.isPending} onClick={() => save.mutate(true)}>
          {existing?.enabled ? 'Save' : 'Turn on'}
        </Button>
        {existing?.enabled ? (
          <Button variant="danger" disabled={save.isPending} onClick={() => save.mutate(false)}>
            Turn off
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}

/** Every charge and settlement, and the outstanding aged. */
function StatementPanel({ organizationId, onError, onChanged }: { organizationId: string; onError: (message: string | null) => void; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('CHECK');
  const [reference, setReference] = useState('');
  const [receivedOn, setReceivedOn] = useState(today());
  const statement = useQuery<HouseAccountStatement, ApiError>({ queryKey: ['house-account', organizationId], queryFn: () => houseAccountEndpoints.statement(organizationId) });

  const settle = useMutation({
    mutationFn: () => houseAccountEndpoints.settle(organizationId, { amount_cents: toCents(amount) ?? 0, method, reference: reference.trim() || null, received_on: receivedOn }),
    onSuccess: () => {
      setAmount('');
      setReference('');
      onError(null);
      void queryClient.invalidateQueries({ queryKey: ['house-account', organizationId] });
      onChanged();
    },
    onError: (cause: ApiError) => onError(cause.message),
  });

  if (!statement.data) return null;
  const { account, lines, aging } = statement.data;

  return (
    <Panel title={`${account.name} — statement`} description="Charges and settlements, oldest first. A settlement is applied to the oldest charges, which is how the aging is worked out.">
      <div className="flex flex-wrap gap-4 border-b border-line px-5 py-3 text-sm">
        {aging.map((bucket) => (
          <span key={bucket.bucket} className={bucket.amount_cents > 0 && bucket.bucket !== 'Current' ? 'text-bad' : 'text-ink-600'}>
            {bucket.bucket}: <span className="tabular font-medium">{dollars(bucket.amount_cents)}</span>
          </span>
        ))}
      </div>
      <Table headers={['Date', 'What', 'Order', 'Amount']}>
        {lines.length === 0 ? (
          <EmptyRow columns={4}>Nothing on this account yet.</EmptyRow>
        ) : (
          lines.map((line, index) => (
            <tr key={`${line.on}-${index}`} className="border-b border-line last:border-0">
              <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{line.on}</td>
              <td className="px-5 py-3 text-sm text-ink-900">{line.description}</td>
              <td className="px-5 py-3 font-mono text-xs text-ink-600">{line.order_number ?? ''}</td>
              <td className={`tabular px-5 py-3 text-sm ${line.amount_cents < 0 ? 'text-ok' : ''}`}>{dollars(line.amount_cents)}</td>
            </tr>
          ))
        )}
      </Table>
      <div className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4">
        <Field label="Amount received" htmlFor="settle-amount">
          <input id="settle-amount" className={`tabular w-28 ${INPUT}`} value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="500.00" />
        </Field>
        <Field label="How" htmlFor="settle-method">
          <select id="settle-method" className={INPUT} value={method} onChange={(event) => setMethod(event.target.value)}>
            {[['CHECK', 'Check'], ['BANK_TRANSFER', 'Bank transfer'], ['CASH', 'Cash'], ['WRITE_OFF', 'Write off'], ['ADJUSTMENT', 'Adjustment']].map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reference" htmlFor="settle-reference" hint={method === 'CHECK' ? 'Check number' : undefined}>
          <input id="settle-reference" className={`w-32 ${INPUT}`} value={reference} onChange={(event) => setReference(event.target.value)} />
        </Field>
        <Field label="Received on" htmlFor="settle-on">
          <input id="settle-on" type="date" className={INPUT} value={receivedOn} onChange={(event) => setReceivedOn(event.target.value)} />
        </Field>
        <Button variant="primary" disabled={settle.isPending || !toCents(amount)} onClick={() => settle.mutate()}>
          Record settlement
        </Button>
        <span className="text-xs text-ink-400">Recorded as at {formatWhen(new Date().toISOString())}; corrections are an adjustment, not an edit.</span>
      </div>
    </Panel>
  );
}
